import type { IconProps } from "@phosphor-icons/react";
import * as PhosphorIcons from "@phosphor-icons/react";
import {
  Article,
  Books,
  Brain,
  Briefcase,
  Certificate,
  CompassTool,
  GameController,
  GraduationCap,
  HandHeart,
  Medal,
  PenNib,
  PuzzlePiece,
  ShareNetwork,
  Translate,
  Trophy,
  User,
  Users,
} from "@phosphor-icons/react";

// Convert "airplane-tilt" → "AirplaneTilt" so we can look up the React component
function kebabToPascal(str: string): string {
  return str
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
}
import type { SectionKey } from "@stylingcv/schema";
import { Tooltip } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import get from "lodash.get";
import React from "react";

import { useResumeStore } from "@/client/stores/resume";

const getSectionIcon = (id: SectionKey, props: IconProps = {}) => {
  switch (id) {
    // Left Sidebar
    case "basics": {
      return <User weight="duotone" size={20} {...props} />;
    }
    case "summary": {
      return <Article weight="duotone" size={20} {...props} />;
    }
    case "awards": {
      return <Medal weight="duotone" size={20} {...props} />;
    }
    case "profiles": {
      return <ShareNetwork weight="duotone" size={20} {...props} />;
    }
    case "experience": {
      return <Briefcase weight="duotone" size={20} {...props} />;
    }
    case "education": {
      return <GraduationCap weight="duotone" size={20} {...props} />;
    }
    case "certifications": {
      return <Certificate weight="duotone" size={20} {...props} />;
    }
    case "interests": {
      return <GameController weight="duotone" size={20} {...props} />;
    }
    case "languages": {
      return <Translate weight="duotone" size={20} {...props} />;
    }
    case "volunteer": {
      return <HandHeart weight="duotone" size={20} {...props} />;
    }
    case "projects": {
      return <PuzzlePiece weight="duotone" size={20} {...props} />;
    }
    case "publications": {
      return <Books weight="duotone" size={20} {...props} />;
    }
    case "hobbies": {
      return <GameController weight="duotone" size={20} {...props} />;
    }
    case "mostProudOf": {
      return <Trophy weight="duotone" size={20} {...props} />;
    }
    case "myMind": {
      return <Brain weight="duotone" size={20} {...props} />;
    }
    // Custom Icon
    case "custom": {
      return props.children;
    }
    case "references": {
      return <Users weight="duotone" size={20} {...props} />;
    }
    case "signature": {
      return <PenNib weight="duotone" size={20} {...props} />;
    }
    // Right Sidebar
    case "template" as any: {
      return <CompassTool weight="duotone" size={20} {...props} />;
    }
    default: {
      if (id.startsWith("custom.")) {
        return props.children;
      }

      return <CompassTool weight="duotone" size={20} {...props} />;
    }
  }
};

type SectionIconProps = IconProps & {
  id: SectionKey;
  name?: string;
  icon?: React.ReactNode;
  className?: string;
  size?: number;
};

export const SectionIcon = ({ id, name, icon, size = 16, className }: SectionIconProps) => {
  const storedName = useResumeStore((state) => {
    try {
      return get(state.resume.data.sections, `${id}.name`, "") as string;
    } catch {
      return "";
    }
  });
  const sectionIcon = useResumeStore((state) => {
    try {
      return get(state.resume.data.sections, `${id}.icon`, "") as string;
    } catch {
      return "";
    }
  });

  // Use || so empty strings fall through to id (?? only skips null/undefined)
  const sectionName = name || storedName || id;

  // Render custom icon if set, otherwise use default
  const renderIcon = () => {
    if (sectionIcon) {
      const componentName = kebabToPascal(sectionIcon.trim());
      const DynamicIcon = (PhosphorIcons as Record<string, any>)[componentName];
      if (typeof DynamicIcon === "function") {
        return <DynamicIcon size={size} weight="regular" />;
      }
      // Unrecognised name — fall through to default below
    }
    const iconNode = getSectionIcon(id, { size });
    if (
      React.isValidElement(iconNode) &&
      typeof iconNode.type === "string" &&
      iconNode.type !== "i"
    ) {
      return <span className="icon-wrapper">{iconNode}</span>;
    }
    return iconNode;
  };

  const iconSpan = (
    <span className={cn("flex size-9 items-center justify-center rounded-full", className)}>
      {icon ?? renderIcon()}
    </span>
  );

  // Don't render tooltip if there's no meaningful label (avoids empty black box)
  if (!sectionName) return iconSpan;

  return (
    <Tooltip side="right" content={sectionName}>
      {iconSpan}
    </Tooltip>
  );
};
